import React, { useEffect, useState } from "react";
import { Plane, Loader2, ExternalLink, Trash2, Trophy } from "lucide-react";
import { travelSearch, travelList, travelDelete } from "../lib/api";
import { toast } from "sonner";

const TYPES = ["flight", "train", "event", "bus", "other"];
const inp = "bg-[#0a1020] border border-[#00F0FF22] rounded-xl px-3 py-2 text-cyan-50 text-sm outline-none min-w-0";

const TravelCard = ({ rec, onDelete }) => {
  const r = rec.result || {};
  return (
    <div className="bg-[#060b18] border border-[#00F0FF22] rounded-2xl p-4 space-y-3" data-testid="travel-result-item">
      <div className="flex items-start gap-3">
        <p className="text-cyan-50 font-bold flex-1">{r.title}{r.portals?.length ? <span className="text-cyan-300/60 font-normal text-sm"> · {r.from} → {r.to} · {r.depart}</span> : null}</p>
        <button onClick={() => onDelete(rec.id)} data-testid="travel-delete" className="text-cyan-200/40 hover:text-rose-300"><Trash2 className="w-4 h-4" /></button>
      </div>
      <div className="space-y-1.5">
        {(r.options || []).map((o, i) => (
          <a key={i} href={o.url || "#"} target="_blank" rel="noreferrer" data-testid={`travel-option-${i}`}
            className={`block px-3 py-2 rounded-lg border ${i === 0 && o.price != null ? "border-emerald-500/50 bg-emerald-500/10" : "border-[#00F0FF1a] bg-[#0a1020] hover:bg-[#00F0FF0a]"}`}>
            <div className="flex items-center gap-2">
              <span className="text-cyan-50 text-sm flex-1 truncate">{i + 1}. {o.name}{o.source ? <span className="text-cyan-300/50"> · {o.source}</span> : null}</span>
              <span className="text-sm font-bold tabular-nums text-cyan-100">{o.price != null ? `$${Math.round(o.price).toLocaleString()}` : "see link"}</span>
              <ExternalLink className="w-3.5 h-3.5 text-cyan-300/40 shrink-0" />
            </div>
            {o.points && <p className="text-[11px] text-cyan-200/60 mt-0.5" data-testid={`travel-points-${i}`}>≈ {o.points.amex_mr.toLocaleString()} Amex MR · {o.points.capone.toLocaleString()} Capital One · {o.points.united.toLocaleString()} United miles</p>}
            {o.details && <p className="text-[11px] text-cyan-300/40">{o.details}</p>}
          </a>
        ))}
      </div>
      {r.portals?.length > 0 && (
        <div className="flex flex-wrap gap-1.5" data-testid="travel-portals">
          {r.portals.map((p) => (
            <a key={p.portal} href={p.url} target="_blank" rel="noreferrer" data-testid={`travel-portal-${p.portal.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className="text-[11px] text-[#00F0FF] border border-[#00F0FF33] rounded-full px-3 py-1 hover:bg-[#00F0FF11] flex items-center gap-1">{p.portal}<ExternalLink className="w-3 h-3" /></a>
          ))}
        </div>
      )}
      <p className="text-sm text-emerald-200 flex items-start gap-1.5" data-testid="travel-verdict"><Trophy className="w-4 h-4 text-emerald-300 shrink-0 mt-0.5" />{r.verdict}</p>
      <p className="text-cyan-300/30 text-[10px]">Points at 1.0¢ Amex MR · 1.0¢ Capital One · 1.2¢ United · {r.source} · links only, Nexus never pays or signs in</p>
    </div>
  );
};

export const TravelPanel = ({ userId }) => {
  const [f, setF] = useState({ from_: "", to: "", depart: "", return_: "", passengers: 1, ticket_type: "flight", query: "" });
  const [busy, setBusy] = useState(false);
  const [items, setItems] = useState([]);
  useEffect(() => { travelList(userId).then((r) => setItems(r.items || [])).catch(() => {}); }, [userId]);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const run = async () => {
    if (!f.query.trim() && !(f.from_.trim() && f.to.trim())) { toast.error("Add from and to (or describe the ticket)"); return; }
    setBusy(true);
    try { const r = await travelSearch(userId, { ...f, passengers: Number(f.passengers) || 1 }); setItems((h) => [r, ...h]); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't search fares right now"); }
    finally { setBusy(false); }
  };
  const del = async (id) => { try { await travelDelete(userId, id); setItems((h) => h.filter((x) => x.id !== id)); } catch (e) {} };
  return (
    <div className="space-y-4" data-testid="travel-panel">
      <div className="bg-[#060b18] border border-[#00F0FF33] rounded-2xl p-3 space-y-2">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          <input value={f.from_} onChange={set("from_")} placeholder="From (JFK)" className={inp} data-testid="travel-from" />
          <input value={f.to} onChange={set("to")} placeholder="To (LAX)" className={inp} data-testid="travel-to" />
          <select value={f.ticket_type} onChange={set("ticket_type")} className={inp} data-testid="travel-type">{TYPES.map((t) => <option key={t} value={t}>{t}</option>)}</select>
          <input type="date" value={f.depart} onChange={set("depart")} className={inp} data-testid="travel-depart" />
          <input type="date" value={f.return_} onChange={set("return_")} className={inp} data-testid="travel-return" />
          <input type="number" min="1" max="9" value={f.passengers} onChange={set("passengers")} className={inp} data-testid="travel-passengers" />
        </div>
        <input value={f.query} onChange={set("query")} placeholder="…or just type it: cheapest flight JFK to LAX May 12 · Knicks tickets Friday" className={`${inp} w-full`} data-testid="travel-query" />
        <button onClick={run} disabled={busy} data-testid="travel-search-btn"
          className="w-full sm:w-auto px-5 py-2.5 rounded-xl text-sm font-bold text-[#030712] bg-[#00F0FF] hover:bg-cyan-300 disabled:opacity-50 flex items-center justify-center gap-2">
          {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Comparing fares…</> : <><Plane className="w-4 h-4" /> Compare prices</>}
        </button>
      </div>
      {items.map((r) => <TravelCard key={r.id} rec={r} onDelete={del} />)}
    </div>
  );
};
